import { useState, useEffect, useCallback } from 'react';
import identityService from '../services/identityService';
import { DEFAULT_PAGE_SIZE } from '../utils/constants';

/**
 * Hook to fetch and manage identities
 * @param {Object} initialFilters - Initial filter values
 * @returns {Object} Identities state and methods
 */
export const useIdentities = (initialFilters = {}) => {
  const [identities, setIdentities] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    page: 1,
    // limit: DEFAULT_PAGE_SIZE,
    search: '',
    type: null,
    status: null,
    riskLevel: null,
    sortBy: 'displayName',
    sortOrder: 'asc',
    ...initialFilters
  });

  const fetchIdentities = useCallback(async (overrideFilters = {}) => {
    setLoading(true);
    setError(null);
    
    const appliedFilters = { ...filters, ...overrideFilters };
    
    try {
      const response = await identityService.getAllIdentities(appliedFilters);
      // console.log("useidentities",response.data.identities)
      if (response.success) {
        setIdentities(response?.data?.identities || []);
        setPagination(response?.data?.pagination || null);
        setMeta(response?.data?.meta || null);
      } else {
        setError(response?.error?.message || 'Failed to fetch identities');
        setIdentities([]);
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
      setIdentities([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchIdentities();
  }, [fetchIdentities]);

  const refreshAll = useCallback(() => {
    fetchIdentities();
  }, [fetchIdentities]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: newFilters?.page !== undefined ? (newFilters?.page ?? 1) : 1 // Reset page on filter change
    }));
  }, []);

  const setPage = useCallback((page) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const setSearch = useCallback((search) => {
    setFilters(prev => ({ ...prev, search, page: 1 }));
  }, []);

  const setSort = useCallback((sortBy, sortOrder = 'asc') => {
    setFilters(prev => ({ ...prev, sortBy, sortOrder }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      page: 1,
      limit: DEFAULT_PAGE_SIZE,
      search: '',
      type: null,
      status: null,
      riskLevel: null,
      sortBy: 'displayName',
      sortOrder: 'asc'
    });
  }, []);

  return {
    identities,
    pagination,
    meta,
    loading,
    error,
    filters,
    fetchIdentities,
    updateFilters,
    setPage,
    setSearch,
    setSort,
    resetFilters,
    refreshAll,
  };
};

export default useIdentities;